
import React, { useCallback, useContext, useMemo, useState } from "react";
import { Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { blue } from "@mui/material/colors";
import { BoxFR } from "components/BoxCustom";
import { GlobalStateContext } from "contexts/GlobalStateContext";
import { jobOrderColNm } from "constants/jobOrderConstant";
import { PLAN_COLORS, SHIPMENT_TYPE } from "constants/serverConstant";
import ComboBox from "components/ComboBox";
import DateTimePickerCustomTime from "components/DateTimePickerCustomTime";
import PlanColorBox from "components/PlanColorBox";
import { containerNoCheck, generateMenuComboItem, getISOCd } from "utils";
import { suggessDrvCombo } from "utils/suggessDrvCombo";
import {
  BookmarkAddedRounded, BookmarkBorderRounded, CreditCardRounded, CreditScoreRounded
  , DescriptionOutlined, DescriptionRounded, DirectionsBoatRounded, MailOutlineRounded, MarkEmailReadRounded
} from "resource/muiImport";


const iconProps = {
  color: "primary",
  sx: { width: 30, height: 30 }
}

const compactSectionSx = { bgcolor: "background.paper", borderRadius: 2, p: 1.5 }
const compactTitleSx = { fontWeight: 700, fontSize: 16, mb: 1.5 }
const compactGridSx = {
  display: "grid", gap: 1.5, gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" }
}
const fullRow = { gridColumn: "1 / -1" }

/** Export/import paperwork state as icon + text, for touch screens where the desktop tooltips never show. */
const StatusItem = ({ done, icon, label }) => (
  <Box sx={{
    display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.5, borderRadius: 1.5,
    bgcolor: done ? blue[50] : "grey.100", color: done ? "primary.main" : "text.secondary",
    border: "1px solid", borderColor: done ? blue[200] : "grey.300"
  }}>
    {icon}
    <Typography sx={{ fontSize: 13, fontWeight: done ? 600 : 400 }}>{label}</Typography>
  </Box>
)

const JODataComponent = ({ joData, setJOData, contStatusData, compact }) => {

  const { msData } = useContext(GlobalStateContext);

  const [isToUpper, setIsToUpper] = useState(true)

  const suggestProcessDrvCombo = useMemo(() => {
    let driverCombo = msData.driverCombo.map(item => ({ ...item }))
    const contStsDrvIds = new Set()
    for (const contSts of contStatusData) {
      contSts.DrvId && contStsDrvIds.add(contSts.DrvId)
    }
    const contStsDrvIdsArr = Array.from(contStsDrvIds)
    for (const drvId of contStsDrvIdsArr) {
      driverCombo = suggessDrvCombo(drvId, driverCombo)
    }
    return driverCombo
  }, [msData.driverCombo, contStatusData])



  const calculateExportIcon = useMemo(() => {
    return (
      <Box display="flex" flexDirection="row" gap={2} alignItems="center" alignSelf="flex-end">
        <Tooltip title={`${joData.IsRdyTake ? "ตู้พร้อมรับ" : "รอเช็คบุคกิ้ง"}`}>
          {joData.IsRdyTake === 0 ? <BookmarkBorderRounded {...iconProps} color="disabled" /> : <BookmarkAddedRounded {...iconProps} />}
        </Tooltip>
        <Tooltip title={`${joData.IsSndMail ? "ส่ง Email แล้ว" : "รอส่ง Email"}`} >
          {joData.IsSndMail === 0 ? <MailOutlineRounded  {...iconProps} color="disabled" /> : <MarkEmailReadRounded  {...iconProps} />}
        </Tooltip>
        <Tooltip title={`${joData.ContDoc ? "มีใบกำกับตู้แล้ว" : "รอใบกำกับตู้"}`}>
          {joData.ContDoc ? <DescriptionRounded  {...iconProps} /> : <DescriptionOutlined {...iconProps} color="disabled" />}
        </Tooltip>
        <Tooltip title={`${joData.ShorId ? "จ่ายชอร์แล้ว" : "รอจ่ายชอร์"}`}>
          {joData.ShorId ? <DirectionsBoatRounded  {...iconProps} /> : <DescriptionOutlined {...iconProps} color="disabled" />}
        </Tooltip>

      </Box>
    )
  }, [joData])


  const calculateImportIcon = useMemo(() => {
    return (
      <Box display="flex" flexDirection="row" gap={2} alignItems="center">
        <Tooltip title="ตู้พร้อมรับ">
          {joData.IsRdyTake === 0 ? <CreditCardRounded {...iconProps} /> : <CreditScoreRounded {...iconProps} />}
        </Tooltip>
      </Box>
    )
  }, [joData])

  const refJobOrderColNm = useMemo(() => {
    const result = { ...jobOrderColNm }
    const refColumnName = msData.serverData.RefColumnName?.job_order || {}
    for (const name in refColumnName) {
      result[name] = refColumnName[name]
    }
    return result
  }, [msData.serverData])

  const dataProp = useCallback((name) => ({
    variant: "outlined",
    size: "small",
    name: name,
    label: refJobOrderColNm[name],
    value: joData[name],
    onChange: (e) => {
      if (name === 'ContSize' || name === 'ContTyp' || name === 'ContHq') {
        let isoCd = "";
        if (name === 'ContSize') isoCd = getISOCd(e.target.value, joData.ContTyp, joData.ContHq);
        else if (name === 'ContTyp') isoCd = getISOCd(joData.ContSize, e.target.value, joData.ContHq);
        else if (name === 'ContHq') isoCd = getISOCd(joData.ContSize, joData.ContTyp, e.target.value);

        setJOData((oldData) => ({
          ...oldData,
          [name]: e.target.value,
          ISOCd: isoCd
        }))
      } else {
        setJOData((oldData) => ({ ...oldData, [name]: e.target.value }))
      }
    },
    onBlur: (e) => {
      const value = isToUpper ? e.target.value.toUpperCase() : e.target.value
      setJOData((oldData) => ({ ...oldData, [name]: value }))
      if (name === "ContNo") {
        containerNoCheck(value);
      }
    }
  }), [refJobOrderColNm, joData, isToUpper, setJOData])

  const datePickerJobOrderProp = useCallback((name, isRequire) => ({
    label: jobOrderColNm[name],
    inputFormat: "DD/MM/YYYY HH:mm",
    mask: "__/__/____ __:__",
    value: joData[name],
    onChange: (newValue) => { setJOData(oldState => ({ ...oldState, [name]: newValue && newValue.format("YYYY-MM-DD HH:mm:00.00") })) },
    renderInput: (params) => <TextField size="small"  {...params} required={isRequire} sx={{ width: compact ? "100%" : 180 }} />,
  }), [joData, setJOData, compact])

  const handleDrvIdChange = useCallback((id) => {
    setJOData(o => ({
      ...o,
      DrvId: id,
      TukId: id ? msData.drivers.find(d => d.DrvId === id)?.TukId || null : null
    }))
  }, [msData.drivers, setJOData])

  if (compact) {
    const statusIcon = { fontSize: 20 }
    const statusItems = joData.ShpmTypId === SHIPMENT_TYPE.EXPORT ? [
      {
        done: !!joData.IsRdyTake, label: joData.IsRdyTake ? "ตู้พร้อมรับ" : "รอเช็คบุคกิ้ง",
        icon: joData.IsRdyTake ? <BookmarkAddedRounded sx={statusIcon} /> : <BookmarkBorderRounded sx={statusIcon} />
      },
      {
        done: !!joData.IsSndMail, label: joData.IsSndMail ? "ส่ง Email แล้ว" : "รอส่ง Email",
        icon: joData.IsSndMail ? <MarkEmailReadRounded sx={statusIcon} /> : <MailOutlineRounded sx={statusIcon} />
      },
      {
        done: !!joData.ContDoc, label: joData.ContDoc ? "มีใบกำกับตู้แล้ว" : "รอใบกำกับตู้",
        icon: joData.ContDoc ? <DescriptionRounded sx={statusIcon} /> : <DescriptionOutlined sx={statusIcon} />
      },
      {
        done: !!joData.ShorId, label: joData.ShorId ? "จ่ายชอร์แล้ว" : "รอจ่ายชอร์",
        icon: joData.ShorId ? <DirectionsBoatRounded sx={statusIcon} /> : <DescriptionOutlined sx={statusIcon} />
      },
    ] : [
      {
        done: !!joData.IsRdyTake, label: joData.IsRdyTake ? "ตู้พร้อมรับ" : "ตู้ยังไม่พร้อมรับ",
        icon: joData.IsRdyTake ? <CreditScoreRounded sx={statusIcon} /> : <CreditCardRounded sx={statusIcon} />
      },
    ]
    return (
      <>
        <Box component="section" sx={compactSectionSx}>
          <Typography sx={compactTitleSx}>นัดหมายและผู้รับผิดชอบ</Typography>
          <Box sx={compactGridSx}>
            <Box sx={fullRow}>
              <DateTimePickerCustomTime {...datePickerJobOrderProp("AptTm", false)} />
            </Box>
            <ComboBox sx={{ width: "100%" }} options={suggestProcessDrvCombo} label={"ผู้รับผิดชอบ"}
              selectedId={joData.DrvId}
              setSelectedId={handleDrvIdChange}
            />
            <ComboBox options={msData.truckCombo} label="ทะเบียนรถ" sx={{ width: "100%" }}
              selectedId={joData.TukId}
              setSelectedId={(id) => setJOData(o => ({ ...o, TukId: id }))}
            />
            <ComboBox sx={{ width: "100%", ...fullRow }} options={msData.genCombo} label={refJobOrderColNm.GenId}
              selectedId={joData.GenId}
              setSelectedId={(id) => setJOData(o => ({ ...o, GenId: id }))}
            />
            {joData.PrId ?
              <Button variant="outlined" sx={{ ...fullRow, minHeight: 44 }}
                onClick={() => window.open(`/emily/DailyJob/${joData.PrId}`, "_blank")}>
                เปิดใบงานหลัก {joData.PrId}
              </Button>
              : null
            }
          </Box>
        </Box>

        <Box component="section" sx={compactSectionSx}>
          <Typography sx={compactTitleSx}>ข้อมูลตู้</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
            {statusItems.map(item => <StatusItem key={item.label} {...item} />)}
          </Box>
          <Box sx={compactGridSx}>
            <TextField {...dataProp("ContNo")} fullWidth />
            <TextField {...dataProp("SealNo")} fullWidth />
            <TextField select {...dataProp("ContSize")} required fullWidth>{generateMenuComboItem(msData.contSizeCombo)}</TextField>
            <TextField select {...dataProp("ContTyp")} required fullWidth>{generateMenuComboItem(msData.contTypCombo)}</TextField>
            <TextField select {...dataProp("ContHq")} required fullWidth>
              <MenuItem value={0}>Std</MenuItem>
              <MenuItem value={1}>HQ</MenuItem>
            </TextField>
            <TextField select {...dataProp("TlrTypId")} required fullWidth>{generateMenuComboItem(msData.tlrTypCombo)}</TextField>
            <TextField {...dataProp("TareWt")} fullWidth inputProps={{ inputMode: "decimal" }} />
            <TextField {...dataProp("FullWt")} fullWidth inputProps={{ inputMode: "decimal" }} />
            <TextField {...dataProp("ISOCd")} fullWidth />
            <FormControlLabel sx={{ m: 0, gap: 1, ...fullRow }}
              label="แปลงเป็นตัวพิมพ์ใหญ่ (ภาษาอังกฤษ)"
              control={<Checkbox sx={{ p: 0.5 }} checked={isToUpper} onChange={(e) => setIsToUpper(e.target.checked)} />}
            />
          </Box>
        </Box>

        <Box component="section" sx={compactSectionSx}>
          <Typography sx={compactTitleSx}>แผนงานและหมายเหตุ</Typography>
          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "76px minmax(0, 1fr)" }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="plnClrLabelCompact">{refJobOrderColNm.PlnClr}</InputLabel>
              <Select label={refJobOrderColNm.PlnClr} labelId="plnClrLabelCompact"
                value={joData.PlnClr}
                onChange={(e) => setJOData(oldData => ({ ...oldData, PlnClr: e.target.value }))}>
                {PLAN_COLORS.map(item => (
                  <MenuItem key={item} value={item}>
                    <PlanColorBox color={item} /></MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField {...dataProp("PlnTxt")} fullWidth />
            <TextField {...dataProp("Rmk")} fullWidth multiline minRows={2} sx={fullRow} />
            <TextField {...dataProp("InvNote")} fullWidth sx={fullRow} />
          </Box>
        </Box>
      </>
    )
  }

  return (
    <>
      <BoxFR>
        {/* <Typography variant="h6" >ข้อมูลใบงาน</Typography> */}
        <DateTimePickerCustomTime {...datePickerJobOrderProp("AptTm", false)} />

        <ComboBox sx={{ width: 170 }} options={suggestProcessDrvCombo} label={"ผู้รับผิดชอบ"}
          selectedId={joData.DrvId}
          setSelectedId={handleDrvIdChange}
        />
        <ComboBox options={msData.truckCombo} label="ทะเบียนรถ"
          sx={{ width: 110 }}
          selectedId={joData.TukId}
          setSelectedId={(id) => {
            setJOData(o => ({
              ...o,
              TukId: id
            }))
          }}
        />
        <ComboBox sx={{ flex: 1 }} options={msData.genCombo} label={refJobOrderColNm.GenId}
          selectedId={joData.GenId}
          setSelectedId={(id) => setJOData(o => ({ ...o, GenId: id }))}
        />
        <Button variant="contained" disabled={!joData.PrId} sx={{ width: 150, px: 0 }}
          onClick={() => window.open(`/emily/DailyJob/${joData.PrId}`, "_blank")}>
          ใบงานหลัก: {joData.PrId ? joData.PrId : "(ไม่มี)"}
        </Button>
        <FormControlLabel sx={{ m: 0 }}
          label="ใช้อักษรพิมพ์ใหญ่(ภาษาอังกฤษ)"
          control={<Checkbox sx={{ p: 0 }} checked={isToUpper} onChange={(e) => setIsToUpper(e.target.checked)} />}
        />
        {
          joData.ShpmTypId === SHIPMENT_TYPE.EXPORT ?
            calculateExportIcon : calculateImportIcon
        }
      </BoxFR>
      <BoxFR>

        <FormControl sx={{ width: 75, minWidth: 75 }} size="small">
          <InputLabel id="plnClrLabel">{refJobOrderColNm.PlnClr}</InputLabel>
          <Select label={refJobOrderColNm.PlnClr} labelId="plnClrLabel"
            value={joData.PlnClr}
            onChange={(e) => setJOData(oldData => ({ ...oldData, PlnClr: e.target.value }))}>
            {PLAN_COLORS.map(item => (
              <MenuItem key={item} value={item}>
                <PlanColorBox color={item} /></MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField {...dataProp("PlnTxt")} />

        <TextField {...dataProp("ContNo")} sx={{ width: 150 }} />
        <TextField {...dataProp("SealNo")} sx={{ width: 150 }} />

        <TextField select {...dataProp("ContSize")} required sx={{ width: 80 }} >{generateMenuComboItem(msData.contSizeCombo)}</TextField>
        <TextField select {...dataProp("ContTyp")} required sx={{ width: 80 }} >{generateMenuComboItem(msData.contTypCombo)}</TextField>
        <TextField select {...dataProp("ContHq")} required sx={{ width: 80 }} >
          <MenuItem value={0}>Std</MenuItem>
          <MenuItem value={1}>HQ</MenuItem>
        </TextField>
        <TextField select {...dataProp("TlrTypId")} required >{generateMenuComboItem(msData.tlrTypCombo)}</TextField>
        <TextField {...dataProp("TareWt")} sx={{ width: 80 }} />
        <TextField {...dataProp("FullWt")} sx={{ width: 80 }} />
        <TextField {...dataProp("ISOCd")} sx={{ width: 80 }} />
      </BoxFR>

      <BoxFR>
        <TextField {...dataProp("Rmk")} sx={{ flex: 1 }} />
        <TextField {...dataProp("InvNote")} sx={{ width: 250 }} />
      </BoxFR>
    </>
  )
}

export default React.memo(JODataComponent);
